import React, {useState} from 'react';
import {SiMicrosoftexcel} from 'react-icons/si';
import {UseInfiniteQueryResult} from 'react-query';
import {useNavigate} from 'react-router-dom';
import {NestedKeys} from 'shared/types/key-manipulation';
import {SelectDataProps} from 'shared/types/select-data';
import {FilterQueryProps} from 'shared/util/encode-filters';
import {translatedProps} from 'shared/util/translated-props';
import Button from '../button';
import FilterList from '../filter-list';
import IconButton, {IconButtonProps} from '../icon-button';
import {HeaderColumn, ProjectionSelect} from './components';
import useTableState, {OrderByProps, PaginationDataProps} from './state';

import {
  BaseTable,
  Container,
  EmptyColumn,
  FixedHeaderColumn,
  HeaderRow,
  Info,
  InfoRow,
  Loader,
  LoaderContainer,
  Row,
  TableBody,
  TableHeader,
  ToolBar,
  PlaceholderHeaderColumn,
  ExpandedRow,
} from './styles';

import {ScrollArea} from '@mantine/core';

import {
  FilterOptionProps,
  FilterTypeEnum,
} from 'shared/migration-components/filter-list/types';
import {FiDownload} from 'react-icons/fi';
import TableRow from './components/table-row';

export type RenderColumnProps<T = any> = (data: T) => React.ReactNode | string;

export type ColumnProps<T = any> = {
  value?: RenderColumnProps<T>;
  visible?: (data: T) => boolean | boolean;
  permission?: string;
  clamp?: number;
  stopPropagation?: boolean;
};

export type ProjectionOptionProps<T = any> = {
  label?: string;
  value?: NestedKeys<T>;
  column?: ColumnProps<T> | RenderColumnProps<T>;
  filterKey?: string;
  filter?: {
    values?: SelectDataProps[];
    centerValue?: boolean;
    type?: FilterTypeEnum;
    valueAsNumber?: boolean;
    search?: boolean;
    dateInterval?: boolean;
  };
  hideFilter?: boolean;
  hidden?: boolean;
};

export interface ProjectionProps<T = any> {
  options: (NestedKeys<T> | ProjectionOptionProps<T>)[];
  values?: NestedKeys<T>[];
  onChange?: (value: NestedKeys<T>[]) => void;
  filter?: {
    onChange: (filters: FilterQueryProps) => void;
    filters: FilterQueryProps;
  };
  orderBy?: {
    field: string;
    order: number;
    onChange: (orderBy: OrderByProps) => void;
  };
}

export interface TableProps<T> {
  url: string;
  projection: ProjectionProps<T>;
  onRowClick?: (row: T) => void;
  fetchFilters?: string[];
  translatedPropsKey: keyof typeof translatedProps;
  inifityQuery: UseInfiniteQueryResult<PaginationDataProps>;
  ExpandedComponent?: ((data: T) => React.ReactNode) | React.ReactNode;
  toolbar?: IconButtonProps[];
  importData?: boolean;
  canExportData?: boolean;
  extraParams?: any;
}

const ApiTable: <T>(p: TableProps<T>) => React.ReactElement<T> = ({
  onRowClick,
  projection,
  fetchFilters,
  url,
  translatedPropsKey,
  inifityQuery,
  ExpandedComponent,
  toolbar,
  canExportData = true,
  importData,
  extraParams,
}) => {
  const {
    hasNextPage,
    loadingExportData,
    handleExportData,
    filters,
    data,
    isLoading,
    count,
    isFetchingNextPage,
    fetchNextPage,
    handleChangeOrder,
    reorderProjection,
    handleGetTemplate,
    handleImportData,
    loadingImportData,
    loadingTemplate,
    importInputRef,
    reorderRows,
  } = useTableState({
    projection,
    url,
    fetchFilters,
    inifityQuery,
    extraParams,
    reorderIndexes: true,
    translatedPropsKey,
  });

  const [expanded, setExapanded] = useState<number>();
  const navigation = useNavigate();
  const getNestedKey: any = (object: any, key: string) => {
    const [firstKey, ...rest] = key.split('.');

    if (rest.length === 0) return object?.[firstKey];
    return getNestedKey(object[firstKey], rest.join('.'));
  };

  const getHeaderLabel = (option: NestedKeys | ProjectionOptionProps) => {
    if (typeof option === 'string')
      return getNestedKey(translatedProps[translatedPropsKey], option);
    if (option.label) return option.label;
    return getNestedKey(translatedProps[translatedPropsKey], option.value);
  };

  const getProjectionOption = (projectionKey: NestedKeys) =>
    projection.options
      .filter((option) => typeof option === 'string' || !option.hidden)
      .find(
        (option) =>
          (typeof option !== 'object' ? option : option.value) ===
          projectionKey,
      )!;

  const resolvePath = (object: any, path: string) =>
    path?.split('.').reduce((o, p) => (o ? o[p] : undefined), object);

  const filterOptions: FilterOptionProps[] = projection?.options
    .filter(
      (option) =>
        typeof option === 'string' || (!option.hidden && !option.hideFilter),
    )
    .map((option) => {
      const label = getHeaderLabel(option);
      const field = typeof option === 'string' ? option : option.value!;
      const type =
        typeof option !== 'string' ? option.filter?.type ?? 'text' : 'text';
      let filter = (
        typeof option === 'object' ? option.filter ?? {} : {}
      ) as ProjectionOptionProps['filter'];

      const values = resolvePath(filters, field);

      if (filters && values) filter = {...filter, values, type: 'select'};

      return {label, field, type, ...filter};
    })
    .filter((option) => option);

  return (
    <>
      {projection?.filter && (
        <FilterList
          {...{
            filterOptions,
            ...projection.filter,
          }}
        />
      )}
      <Container>
        <InfoRow>
          {!isLoading && data.length > 0 && (
            <Info defaultValue={'row'}>
              Mostrando {data.length} resultados do total de {count} ocorrências
            </Info>
          )}
          <ToolBar>
            {importData && (
              <>
                <IconButton
                  {...{
                    icon: <FiDownload />,
                    label: 'Modelo de importação',
                    loading: loadingTemplate,
                    onClick: handleGetTemplate,
                  }}
                />
                <IconButton
                  {...{
                    icon: <SiMicrosoftexcel />,
                    label: 'Importar dados',
                    loading: loadingImportData,
                    onClick: () => importInputRef.current?.click(),
                  }}
                />
                <input
                  ref={importInputRef}
                  hidden
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => handleImportData(e.target.files![0])}
                />
              </>
            )}
            {toolbar?.map((iconButton, index) => (
              <IconButton key={index} {...iconButton} />
            ))}
            {canExportData && (
              <IconButton
                {...{
                  icon: <SiMicrosoftexcel />,
                  label: 'Exportar',
                  loading: loadingExportData,
                  onClick: handleExportData,
                }}
              />
            )}
            {projection?.values && projection.onChange && (
              <ProjectionSelect {...{...projection, translatedPropsKey}} />
            )}
          </ToolBar>
        </InfoRow>
        <ScrollArea
          style={{
            width: '100%',
          }}>
          <BaseTable>
            <TableHeader>
              <HeaderRow>
                {ExpandedComponent && <PlaceholderHeaderColumn />}
                {projection?.values &&
                  projection.values
                    .filter((value) => getProjectionOption(value))
                    .map((value, index) => (
                      <HeaderColumn
                        key={index}
                        label={getHeaderLabel(getProjectionOption(value))}
                        {...{
                          onChange: handleChangeOrder,
                          field: value,
                          orderBy: projection.orderBy,
                          reorderProjection,
                        }}
                      />
                    ))}
                {!projection?.values &&
                  projection.options.map((option, index) => (
                    <FixedHeaderColumn key={index}>
                      {getHeaderLabel(option)}
                    </FixedHeaderColumn>
                  ))}
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {!isLoading &&
                data.length > 0 &&
                data.map((row, index) => (
                  <TableRow
                    key={index}
                    {...{
                      data: row,
                      projection,
                      translatedPropsKey,
                      onRowClick: () =>
                        onRowClick ? onRowClick(row) : navigation(row.id),
                      reorderRows,
                      draggable: projection.orderBy?.field === 'orderIndex',
                    }}
                  />
                  // {ExpandedComponent && expanded === index && (
                  //   <ExpandedRow>
                  //     <td colSpan={'100%' as any}>
                  //       {typeof ExpandedComponent === 'function'
                  //         ? ExpandedComponent(row)
                  //         : ExpandedComponent}
                  //     </td>
                  //   </ExpandedRow>
                  // )}
                ))}
              {!isLoading && data.length === 0 && (
                <Row>
                  <EmptyColumn>Nenhum dado encontrado</EmptyColumn>
                </Row>
              )}
              {isLoading && (
                <Row>
                  <LoaderContainer>
                    <Loader />
                  </LoaderContainer>
                </Row>
              )}
            </TableBody>
          </BaseTable>
        </ScrollArea>
        {!isLoading && hasNextPage && (
          <Container>
            <Info defaultValue={'column'}>
              Mostrando {data.length} resultados do total de {count} ocorrências
            </Info>
            {
              <Button
                title="Mostrar mais"
                widthMode="fit-content"
                size="small"
                onClick={fetchNextPage}
                loading={isFetchingNextPage}
              />
            }
          </Container>
        )}
      </Container>
    </>
  );
};

export default ApiTable;
